import React, { useState } from "react";
import { Image, Wand2, FileText, Trash2, Scissors, ListVideo, Save, Ungroup, PackageCheck, RefreshCw } from "lucide-react";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import { useLastPresentValue } from "../../../hooks/useLastPresentValue";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { getVideoProviderLabel } from "../../../utils/projectRuntime";
import { getEffectiveAssetMeta } from "../../../utils/guaVideoAssetStatus";

// 通用菜单按钮
function MenuBtn({ icon, label, color, muted, onClick }) {
  return (
    <button
      className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-xs transition-colors duration-150 rounded-md"
      style={{ color: muted ? 'var(--color-text-muted)' : 'var(--color-text-primary)' }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      onClick={onClick}
    >
      <span style={{ color: color || 'var(--color-text-muted)' }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function Divider() {
  return <div className="h-px my-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />;
}

// 节点右键菜单
// props:
//   menu: { x, y, node } | null
//   onClose: () => void
//   actions: { expandNode, handleDeleteNode, setImagePreview, setShowGenerateTypeMenu, handleAddToRoughCut, canRoughCut, screenToFlowPosition, reactFlowWrapperRef, nodes }
export default function NodeContextMenu({ menu, onClose, actions }) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // 出场动画：延迟卸载（缓存 menu 供退场渲染）
  const { mounted, exiting } = useDelayedUnmount(!!menu, 150);
  // 缓存最后一份 menu，供退场动画渲染最后一帧
  const effectiveMenu = useLastPresentValue(menu);
  if (!mounted || !effectiveMenu) return null;

  const node = effectiveMenu.node;
  const nodeType = node.data?.legacyType || node.type;
  const data = node.data || {};

  const doGenerate = () => {
    const wrapper = actions.reactFlowWrapperRef.current;
    if (!wrapper) return;
    const bounds = wrapper.getBoundingClientRect();
    const canvasPosition = actions.screenToFlowPosition({
      x: effectiveMenu.x - bounds.left,
      y: effectiveMenu.y - bounds.top,
    });
    actions.setShowGenerateTypeMenu({
      x: effectiveMenu.x,
      y: effectiveMenu.y,
      sourceNodeIds: [node.id],
      allSelectedNodeIds: [node.id],
      position: canvasPosition,
    });
    onClose();
  };

  const doDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const doExpand = () => {
    actions.expandNode(node.id);
    onClose();
  };

  // 通用操作（所有节点都有）
  const commonActions = (
    <>
      <MenuBtn icon={<FileText size={16} />} label="编辑节点" color={'var(--color-blue-primary)'} onClick={doExpand} />
      <Divider />
      <MenuBtn icon={<Trash2 size={16} />} label="删除节点" muted onClick={doDelete} />
    </>
  );

  // 获取节点图片 URL（不同类型取法不同；generate 优先 P2-1 的 lastResultUrl）
  const getImageUrl = () => {
    if (nodeType === 'resource' || nodeType === 'atom') return data.url;
    if (nodeType === 'generate') {
      return data.currentGenerationHistoryId
        ? data.lastResultUrl || data.result
        : data.url || data.lastResultUrl || data.result;
    }
    if (nodeType === 'storyboardShot') return data.generatedImage;
    if (nodeType === 'storyboardGrid') return data.gridImageUrl;
    return data.url || data.lastResultUrl || data.result || data.thumbnail;
  };

  const imageUrl = getImageUrl();
  const isImage = data.resourceType === 'image' || data.generateType === 'image' || nodeType === 'storyboardShot' || nodeType === 'storyboardGrid';

  // 标题
  const getTitle = () => {
    if (nodeType === 'resource') {
      const t = data.resourceType;
      return t === 'image' ? '图片资源' : t === 'video' ? '视频资源' : t === 'audio' ? '音频资源' : '资源节点';
    }
    if (nodeType === 'generate') return data.generateType === 'image' ? '图片生成' : '视频生成';
    if (nodeType === 'storyboardGrid') return `分镜生成 (${data.rows || 2}×${data.cols || 2})`;
    if (nodeType === 'storyboardShot') return `分镜 #${data.shotNumber}`;
    if (nodeType === 'seedanceMerge') return `视频片段 (${data.sourceShots?.length || 0} 个分镜)`;
    if (nodeType === 'storyboardEpisode') return '整集分镜';
    if (nodeType === 'groupFrame') return `分组框${data.name ? ` · ${data.name}` : ''}`;
    if (nodeType === 'stickyNote') return '便签';
    if (nodeType === 'aiDirector') return 'AI 导演';
    return '节点';
  };

  return (
    <>
      <div
        className={`absolute z-50 rounded-lg ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-150"}`}
        style={{
          left: effectiveMenu.x,
          top: effectiveMenu.y,
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)',
          minWidth: 180,
          position: 'fixed',
          boxShadow: 'var(--shadow-dropdown)',
        }}
      >
        <div className="p-2">
        <div className="text-label mb-2 px-2">
          {getTitle()}
        </div>

        {/* 查看资源/结果 */}
        {imageUrl && (
          <MenuBtn
            icon={<Image size={16} />}
            label="查看"
            color={'var(--color-green-primary)'}
            onClick={() => {
              actions.setImagePreview({
                url: imageUrl,
                name: data.name || data.prompt || '预览',
                mediaType: data.resourceType === 'video' || data.generateType === 'video' ? 'video' : 'image',
              });
              onClose();
            }}
          />
        )}

        {/* 基于此生成（有图片的节点） */}
        {imageUrl && (
          <MenuBtn icon={<Wand2 size={16} />} label="基于此生成" color={'var(--color-accent-primary)'} onClick={doGenerate} />
        )}

        {/* 拆分宫格（图片资源节点） */}
        {isImage && imageUrl && (nodeType === 'resource' || nodeType === 'atom') && (
          <MenuBtn
            icon={<Scissors size={16} />}
            label="拆分宫格"
            color={'var(--color-accent-primary)'}
            onClick={() => {
              actions.handleSplitImage?.(node);
              onClose();
            }}
          />
        )}

        {/* 入库到素材库（图片节点；生成视频时用 assetId 引用，图生图不需要） */}
        {isImage && imageUrl && (() => {
          const assetMeta = getEffectiveAssetMeta(data, actions.assetProvider);
          if (!assetMeta.provider) return null;
          const status = assetMeta.status;
          const providerLabel = getVideoProviderLabel(assetMeta.provider);
          if (status === 'Active') {
            return <MenuBtn icon={<PackageCheck size={16} />} label={`已入库 · ${providerLabel}`} color={'var(--color-success)'} muted onClick={() => {}} />;
          }
          const label = status === 'Processing'
            ? `${providerLabel} 入库中 · 刷新状态`
            : status === 'Failed'
              ? `${providerLabel} 入库失败 · 重试`
              : `入库到 ${providerLabel} 素材库`;
          const icon = status === 'Processing' ? <RefreshCw size={16} /> : <PackageCheck size={16} />;
          return (
            <MenuBtn
              icon={icon}
              label={label}
              color={status === 'Failed' ? 'var(--color-error)' : 'var(--color-green-primary)'}
              onClick={() => {
                actions.handleIngestNodeImage?.(node);
                onClose();
              }}
            />
          );
        })()}

        {/* P3-1 保存到资源中心（resource 且尚未入资源中心的节点） */}
        {nodeType === 'resource' && !data.resourceId && imageUrl && (
          <MenuBtn
            icon={<Save size={16} />}
            label="保存到资源中心"
            color={'var(--color-blue-primary)'}
            onClick={() => {
              actions.handleSaveToResourceCenter?.(node);
              onClose();
            }}
          />
        )}

        {/* 加入粗剪台（有图片的节点；粗剪台是按集功能，项目级画布下隐藏） */}
        {actions.canRoughCut && isImage && imageUrl && (
          <MenuBtn
            icon={<ListVideo size={16} />}
            label="加入粗剪台"
            color={'var(--color-green-primary)'}
            onClick={() => {
              actions.handleAddToRoughCut?.(imageUrl, data.name || data.prompt || '资源');
              onClose();
            }}
          />
        )}

        {/* 片4 移出分组（在组内的节点） */}
        {(node.parentId || data.parentId) && (
          <MenuBtn
            icon={<Ungroup size={16} />}
            label="移出分组"
            color={'var(--color-purple-primary)'}
            onClick={() => {
              actions.handleRemoveFromGroup?.(node);
              onClose();
            }}
          />
        )}

          {commonActions}
        </div>
      </div>
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          actions.handleDeleteNode(node.id);
          onClose();
        }}
        title="删除节点"
        message="确定删除这个节点吗？关联连线也会一并移除。"
        confirmLabel="删除"
        variant="danger"
      />
    </>
  );
}
